/* This file should be combined with the CreateDialog that is used everywhere else
 However at the moment it is needed because of the special behaviour here which
 loads the geometry down so that it can be edited */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    MenuItem,
    makeStyles,
    InputAdornment
} from '@material-ui/core';

import { LibraryAdd as AddSiteCardIcon } from '@material-ui/icons';

import { useDispatch, useSelector } from 'react-redux';
import {
    createSiteCardAsync,
    createSiteCardFromFeatureAnalysisAsync
} from '../../../redux/site_card/Actions';
import { MAIN_MAP } from '../../../redux/map/Constants';
import { setShapeFeatureProperty } from '../../../redux/map/Actions';

import SelectTextField from '../UI/SelectTextField';
import TextField from '../UI/TextField';
import DialogButton from '../UI/Dialog/DialogButton';

const propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    featureConfig: PropTypes.oneOfType([
        PropTypes.shape({
            layerId: PropTypes.number.isRequired
        }),
        PropTypes.shape({
            layerId: PropTypes.number.isRequired,
            featureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
        }),
        PropTypes.shape({
            layerId: PropTypes.number.isRequired,
            featureIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
                .isRequired
        }),
        PropTypes.shape({
            isDrawn: PropTypes.bool.isRequired
        })
    ]).isRequired,
    geometry: PropTypes.object,
    defaultName: PropTypes.string.isRequired,
    featureAnalysisId: PropTypes.number
};

const defaultProps = {
    geometry: null,
    featureAnalysisId: null
};

const useStyles = makeStyles(theme => styles(theme));

// When featureAnalysisId is included, we assume that the site card is been created from a feature analysis
// When featureAnalysisId is not included, we assume featureConfig is included and a weighting profile is needed to create a site card
function CreateDialog({ open, onClose, featureConfig, geometry, defaultName, featureAnalysisId }) {
    const dispatch = useDispatch();
    const classes = useStyles();

    const MAX = 1000;
    const MIN = 1;
    const currentProject = useSelector(state => state.project.currentProject);
    const drawingFeature = useSelector(state => state[MAIN_MAP].drawingFeature);
    const currentProjectId = currentProject?.id;
    const projectStructure = currentProject.structure;

    const [name, setName] = useState(defaultName);
    const [description, setDescription] = useState('');
    const [layerId, setLayerId] = useState(featureConfig.layerId || '');
    const [columnId, setColumnId] = useState(null);
    const [error, setError] = useState(false);
    const [bufferValue, setBufferValue] = useState(1);

    const drawnLayerChoices = useSelector(state => {
        return state.user.features['drawnSiteCard']?.layers?.map(drawnSiteCardLayer =>
            state.layers.layers.find(layer => layer.id === drawnSiteCardLayer.layerId)
        );
    });
    const analysisProfiles = useSelector(state => {
        const profileIds =
            state.user.features['analysis']?.layers?.find(l => l.layerId === layerId)?.profiles ||
            [];
        return state.lookups.uiData.analysisProfiles.filter(ap => profileIds.includes(ap.id));
    });
    const defaultAnalysisProfile = useSelector(state => {
        const defaultProfiles = state?.user?.prefs?.defaultAnalysisProfiles ?? [];
        const profile = defaultProfiles.find(profile => profile.layerId === layerId);

        if (profile) return profile.profileId;

        return analysisProfiles.length > 0 ? analysisProfiles[0].id : '';
    });

    const [analysis, setAnalysis] = useState(defaultAnalysisProfile);

    const dispatchCreateSiteCard = (projectId, name, columnId, params) =>
        dispatch(createSiteCardAsync(projectId, name, columnId, params));
    const dispatchCreateSiteCardFromFeatureAnalysis = (featureAnalysisId, name, columnId, params) =>
        dispatch(createSiteCardFromFeatureAnalysisAsync(featureAnalysisId, name, columnId, params));
    function dispatchSetShapeFeatureProperty(userProperty, userPropertyValue) {
        return dispatch(
            setShapeFeatureProperty(drawingFeature?.id, userProperty, userPropertyValue, MAIN_MAP)
        );
    }

    useEffect(() => {
        const firstColumn = projectStructure.columns[projectStructure.columnOrder[0]];
        setColumnId(firstColumn?.id);
    }, [currentProject]);

    function createSiteCard() {
        // Set buffer value to feature property prior to saving
        if (bufferValue > 0 && geometry && geometry.type === 'LineString') {
            dispatchSetShapeFeatureProperty('buffer', bufferValue);
        }

        // If a feature analysis has been included then creating the site card from a feature analysis
        if (featureAnalysisId != null) {
            dispatchCreateSiteCardFromFeatureAnalysis(featureAnalysisId, name, columnId, {
                description: description
            });
        } else if (featureConfig.isDrawn) {
            dispatchCreateSiteCard(currentProjectId, name, columnId, {
                description: description,
                weightingProfileId: analysis,
                ...featureConfig,
                layerId: layerId,
                geometry: geometry,
                buffer:
                    bufferValue > 0 && geometry && geometry.type === 'LineString'
                        ? bufferValue
                        : undefined
            });
        } else {
            dispatchCreateSiteCard(currentProjectId, name, columnId, {
                description: description,
                weightingProfileId: analysis,
                ...featureConfig,
                geometry: geometry
            });
        }
        onClose();
    }

    function bufferNumber(number) {
        const input = Number(number);
        const isValid = input >= MIN && input <= MAX;
        setError(!isValid);

        if (isValid) {
            setBufferValue(input);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle disableTypography>
                <AddSiteCardIcon />
                <Typography variant="h4">Create site card</Typography>
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    name="name"
                    placeholder={defaultName}
                    autoFocus
                    fullWidth
                    label="Site card name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                />
                <TextField
                    name="description"
                    fullWidth
                    label="Site card description"
                    placeholder="Optional"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    multiline
                    minRows={2}
                />
                {((geometry && geometry.type === 'LineString') ||
                    projectStructure.columnOrder.length > 1) && (
                    <div className={classes.adjustments}>
                        {geometry && geometry.type === 'LineString' && (
                            <TextField
                                label="Buffer"
                                tooltip="This will add a buffer around the drawn line. The max buffer size is 1000m. The buffer area will be included in the site card analysis."
                                type="number"
                                error={error}
                                helperText={error ? 'Buffer must be between 1m and 1000m' : null}
                                fullWidth
                                value={bufferValue}
                                InputProps={{
                                    inputProps: {
                                        max: MAX,
                                        min: MIN
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start">m</InputAdornment>
                                    )
                                }}
                                onKeyDown={e => {
                                    if (
                                        e.key === 'e' ||
                                        e.key === 'E' ||
                                        e.key === '-' ||
                                        e.key === '+'
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                                onChange={e => bufferNumber(e.target.value)}
                            />
                        )}

                        {projectStructure.columnOrder.length > 1 && (
                            <SelectTextField
                                fullWidth
                                label="List"
                                tooltip="You can select any of your lists in your workspace to save the site card to."
                                value={columnId}
                                onChange={e => setColumnId(e.target.value)}>
                                {projectStructure.columnOrder.map(column => (
                                    <MenuItem
                                        key={projectStructure.columns[column].id}
                                        value={projectStructure.columns[column].id}>
                                        {projectStructure.columns[column].title}
                                    </MenuItem>
                                ))}
                            </SelectTextField>
                        )}
                    </div>
                )}
                <div
                    className={
                        (geometry && geometry.type === 'LineString') ||
                        projectStructure.columnOrder.length > 1
                            ? classes.adjustments
                            : null
                    }>
                    {featureConfig.isDrawn && (
                        <SelectTextField
                            fullWidth
                            label="Layer"
                            tooltip="When creating a site card from a drawn shape, you need to associate with a existing land dataset. eg. Use HM Land Registry for England & Wales. Use Inspire for Scotland."
                            value={layerId}
                            onChange={e => setLayerId(e.target.value)}>
                            {drawnLayerChoices?.map(e => (
                                <MenuItem key={e.id} value={e.id}>
                                    {e.name}
                                </MenuItem>
                            ))}
                        </SelectTextField>
                    )}

                    {featureAnalysisId == null && (
                        <SelectTextField
                            fullWidth
                            label="Analysis Profile"
                            value={analysis}
                            disabled={layerId === ''}
                            onChange={e => setAnalysis(e.target.value)}>
                            {analysisProfiles?.map(profile => (
                                <MenuItem key={profile.id} value={profile.id}>
                                    {profile.name}
                                </MenuItem>
                            ))}
                        </SelectTextField>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <DialogButton
                    variant="contained"
                    onClick={createSiteCard}
                    disabled={!(name && layerId && (featureAnalysisId || analysis))}>
                    Create
                </DialogButton>
                <DialogButton onClick={onClose} variant="outlined">
                    Cancel
                </DialogButton>
            </DialogActions>
        </Dialog>
    );
}

const styles = () => ({
    adjustments: {
        display: 'flex',
        gap: 10
    }
});

CreateDialog.propTypes = propTypes;
CreateDialog.defaultProps = defaultProps;

export default CreateDialog;
